import { PersonalInfoDetailsAnswer } from "@physiofit-digital/physiofit_types";
import { useTranslation } from "react-i18next";
import { useConfirmation } from "../../../core/components/confirmationModal/useEventIndicator";
import { useEventIndicator } from "../../../core/components/eventIndicator/useEventIndicator";
import { getPracticePatient, getWebAppChoices, getWebAppCreatedIds } from "../../../core/store/currentFormSlice";
import { useMainStore } from "../../../core/store/mainStore";
import { useHandleParamNavigation } from "../../../core/utils/useHandleParamNavigation";
import { handleLogout } from "../../navigation/utils/handleLogout";
import { changesStoredPerson, storedPersonLabel } from "./personalInfoChange";

type Answer = "otherPerson" | "correction" | "dismissed";

/** The question before "Persönliche Daten" overwrites a stored name or birthday; „Ja" never writes. */
export const useOtherPersonQuestion = () => {
  const { t } = useTranslation();
  const confirm = useConfirmation();
  const eventIndicator = useEventIndicator();
  const navigate = useHandleParamNavigation();
  const leaveRecord = useMainStore((state) => state.leaveRecord);

  const ask = (person: string) =>
    new Promise<Answer>((resolve) =>
      confirm({
        title: t("PERSONAL_INFO_OTHER_PERSON_TITLE"),
        text: t("PERSONAL_INFO_OTHER_PERSON_TEXT", { person }),
        confirmText: t("PERSONAL_INFO_OTHER_PERSON_YES"),
        cancelText: t("PERSONAL_INFO_OTHER_PERSON_NO"),
        resolve: (confirmed) => resolve(confirmed ? "otherPerson" : "correction"),
        // Closing without an answer (a tap outside, a logout) is neither: the second one would overwrite the person.
        onDismiss: () => resolve("dismissed"),
      }),
    );

  /** True: save the typed data on this record. False: nothing is saved (closed, or the patient left the record). */
  const confirmSamePerson = async (typed: PersonalInfoDetailsAnswer): Promise<boolean> => {
    const record = getPracticePatient(useMainStore.getState());
    if (!record) return true;
    // This tab created the record moments ago on the identity step: it holds exactly what the patient just typed,
    // so a change here is a correction, not another person (PF-6134).
    if (getWebAppCreatedIds(useMainStore.getState()).includes(record.id)) return true;
    if (!changesStoredPerson(record.personalInfo, typed)) return true;

    const answer = await ask(storedPersonLabel(record.personalInfo, t));
    if (answer !== "otherPerson") return answer === "correction";
    if (getWebAppChoices(useMainStore.getState())?.length) {
      leaveRecord(true);
      return false;
    }
    // No chooser at the login: the other person signs in anew. PF-6133's release session never asks this question.
    eventIndicator("info", t("PERSONAL_INFO_OTHER_PERSON_SIGN_IN_AGAIN"));
    await handleLogout();
    navigate("/welcome/login", { replace: true });
    return false;
  };

  return { confirmSamePerson };
};
